import { isPeachId } from 'core/helpers'

import { matchCatalog } from './matchCatalog'
import type { PeachIdContext, PeachIdTable, ResourceCatalog } from './types'

/**
 * Param names that a single UI param accepts, most specific first. A loan
 * record names its borrower `mainBorrowerId`, not `personId`.
 */
const PARAM_ALIASES: { [param: string]: Array<string> } = {
  personId: ['personId', 'mainBorrowerId', 'borrowerId'],
}

const asId = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null

/**
 * Reads one template param. The record wins because it describes the entity;
 * route params only describe where the reader happens to be browsing.
 */
const bindParam = (param: string, ctx: PeachIdContext): string | null => {
  for (const name of PARAM_ALIASES[param] ?? [param]) {
    const fromRecord = asId(ctx.record[name])
    if (fromRecord) return fromRecord
  }
  for (const name of PARAM_ALIASES[param] ?? [param]) {
    const fromParams = asId(ctx.params[name])
    if (fromParams) return fromParams
  }
  return null
}

/** Substitutes `:id` and every other `:param`, or returns null if any is unbound. */
export const bindTemplate = (
  template: string,
  id: string,
  ctx: PeachIdContext,
): string | null => {
  let unresolved = false

  const path = template.replace(/:([A-Za-z]+)/g, (_match, param: string) => {
    if (param === 'id') return id
    const bound = bindParam(param, ctx)
    if (!bound) {
      unresolved = true
      return ''
    }
    return bound
  })

  return unresolved ? null : path
}

/**
 * Resolves a value to a UI path, or null when it is not a Peach id, has no
 * known resource, has no template, or is missing an ancestor. Every null
 * renders as plain text.
 */
export const resolvePeachIdLink = (
  table: PeachIdTable,
  id: unknown,
  ctx: PeachIdContext,
  catalog?: ResourceCatalog,
): string | null => {
  if (typeof id !== 'string' || !isPeachId(id)) return null

  const trimmed = id.trim()
  const fromFieldName = ctx.fieldName
    ? matchCatalog(ctx.fieldName, catalog)
    : null
  const key =
    fromFieldName ?? table.byPrefix[trimmed.slice(0, 2).toUpperCase()] ?? null
  if (!key) return null

  const template = table.templates[key]
  if (!template) return null

  return bindTemplate(template, trimmed, ctx)
}
